import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import MUIDataTable from 'mui-datatables'

import { Container, Typography } from 'src/modules/ui'
import { useFetchBuyerInvoicesQuery, useMarkInvoicePaidMutation } from '../api'
import { formatDate, formatPrice } from 'src/utils'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { Grid } from '@material-ui/core'
import { Button } from 'src/modules/ui'
import { isReadyForPayment } from '../utils'
import { API } from 'aws-amplify'
import { showError } from '../../../../notification/slice'

const InvoiceList = ({ title = 'Sent Invoices' }) => {
  const invoicesQuery = useFetchBuyerInvoicesQuery()
  const [markInvoicePaid] = useMarkInvoicePaidMutation()
  const history = useHistory()

  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [selectedRowIndexes, setSelectedRowIndexes] = useState([])

  if (invoicesQuery.isLoading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    )
  }

  const invoices = invoicesQuery.data.results

  const handlePDFExport = async () => {
    try {
      // Loop through selected invoices
      for (const invoiceId of selectedInvoices) {
        // Find the invoice object in your data
        const invoice = invoices.find(inv => inv.id === invoiceId)

        // Check if document URL exists
        if (invoice && invoice.document) {
          // Fetch PDF blob
          const response = await fetch(invoice.document)
          const blob = await response.blob()

          // Create a temporary link element
          const link = document.createElement('a')
          document.body.appendChild(link)

          // Set link properties
          link.style = 'display: none'
          link.href = URL.createObjectURL(blob)
          link.download = `invoice_${invoiceId}.pdf`

          // Trigger download and cleanup
          link.click()
          URL.revokeObjectURL(link.href)
          link.remove()
        }
      }
    } catch (err) {
      showError(err.message)
    }
  }

  const handleExport = async () => {
    try {
      const apiParameters = {
        responseType: 'blob',
      }

      if (selectedInvoices.length > 0) {
        apiParameters.queryStringParameters = {
          selectedInvoices: selectedInvoices.join(','),
        }
      }

      const blob = await API.get(
        'default',
        '/buyer/invoices/export/',
        apiParameters
      )

      const a = document.createElement('a')
      document.body.appendChild(a)
      a.style = 'display: none'

      const csvUrl = URL.createObjectURL(blob)
      a.href = csvUrl
      a.download = 'invoices.csv'
      a.click()
      URL.revokeObjectURL(a.href)
      a.remove()
    } catch (err) {
      showError(err.message)
    }
  }

  const handleInvoiceSelectionChange = (currentSelection, allSelections) => {
    setSelectedInvoices(
      allSelections.map(selection => data[selection.index].id)
    )
    setSelectedRowIndexes(allSelections.map(selection => selection.index))
  }

  const handleRowClick = (rowData, rowMeta, event) => {
    if (event.target.type !== 'checkbox') {
      history.push(`/invoices/${invoices[rowMeta.dataIndex].id}`)
    }
  }

  const data = invoices.map(invoice => {
    const {
      supplier,
      total,
      deliveryCharge,
      goodsTotal,
      invoiceDate,
      dueDate,
      otherCharge,
    } = invoice

    return {
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      supplier: supplier.name,
      goodsTotal: formatPrice(goodsTotal),
      invoiceDate: formatDate(invoiceDate),
      dueDate: formatDate(dueDate),
      deliveryCharge: formatPrice(deliveryCharge),
      otherCharge: formatPrice(otherCharge),
      total: formatPrice(total),
      status: isReadyForPayment(invoice),
      paid: invoice.status === 'paid',
      actions: invoice.id,
    }
  })

  const columns = [
    {
      name: 'invoiceNumber',
      label: 'Invoice Number',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'invoiceDate',
      label: 'Invoice Date',
      options: {
        filter: true,
        sort: true,
        sortDirection: 'asc',
      },
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'supplier',
      label: 'Supplier',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'goodsTotal',
      label: 'Goods Total',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'total',
      label: 'Total',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'status',
      label: 'Ready for Payment',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: dataIndex => {
          return data[dataIndex].status ? (
            <Grid style={{ textAlign: 'center' }}>
              <CheckCircleIcon style={{ color: '#48BA77' }} />
            </Grid>
          ) : null
        },
      },
    },
    {
      name: 'paid',
      label: 'Is Paid',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: dataIndex => {
          return data[dataIndex].paid ? (
            <Grid style={{ textAlign: 'center' }}>
              <CheckCircleIcon style={{ color: '#48BA77' }} />
            </Grid>
          ) : null
        },
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: dataIndex => {
          const i = data[dataIndex]

          return i.paid ? null : (
            <Grid style={{ textAlign: 'center' }}>
              <Button
                size="small"
                color="lightGreen"
                onClick={e => {
                  e.stopPropagation()
                  markInvoicePaid(i)
                }}
              >
                Mark Paid
              </Button>
            </Grid>
          )
        },
      },
    },
  ]

  const options = {
    download: false,
    elevation: 0,
    print: false,
    responsive: 'simple',
    rowsPerPage: 20,
    rowsPerPageOptions: [20, 50, 100],
    selectableRows: 'multiple',
    rowsSelected: selectedRowIndexes,
    viewColumns: false,
    selectToolbarPlacement: 'none',
    onRowClick: handleRowClick,
    onRowsSelect: handleInvoiceSelectionChange,
  }

  return (
    <>
      <Container muiTable>
        <Button
          color="lightGreen"
          onClick={handleExport}
          style={{ marginTop: 20, marginBottom: 10, marginRight: 10 }}
        >
          Export All Invoices
        </Button>
        {selectedInvoices.length > 0 ? (
          <Button
            color="lightGreen"
            onClick={handleExport}
            style={{ marginTop: 20, marginBottom: 10 }}
          >
            Export Selected Invoices
          </Button>
        ) : null}
        {selectedInvoices.length > 0 ? (
          <Button
            color="lightGreen"
            onClick={handlePDFExport}
            style={{ marginTop: 20, marginBottom: 10, marginLeft: 10 }}
          >
            Export Selected Invoices as PDF
          </Button>
        ) : null}

        <MUIDataTable
          columns={columns}
          data={data}
          options={options}
          title={title}
        />
      </Container>
    </>
  )
}

export default InvoiceList
